import { useQuery } from '@tanstack/react-query'
import { SpinnerWithBackground, Text } from '@viapeople/components'
import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { sessionStore } from '../../../sharedStores/sessionStore'
import { trpc } from '../../../utils/trpc'
import { EnpsFeedbackList } from '../components/EnpsFeedbackList'
import { useEnpsStore } from '../store'

/**
 * @fileoverview eNPS Results Tab
 *
 * Display NPS score, trends, and response breakdown
 * Supports company-grouped view for external participants
 */

type ViewMode = 'overall' | 'byCompany'

export const EnpsResultsTab = () => {
	const { enpsId } = useParams<{ enpsId: string }>()
	const { user } = sessionStore()
	const organizationId = user?.organizationId
	const { enpsConfig } = useEnpsStore()

	const [viewMode, setViewMode] = useState<ViewMode>('overall')
	const [expandedCompanies, setExpandedCompanies] = useState<Set<string>>(new Set())
	const [selectedCompany, setSelectedCompany] = useState<string>('all')
	const [companySortOrder, setCompanySortOrder] = useState<'desc' | 'asc' | 'none'>('none')

	const { data: results, isLoading } = useQuery({
		...trpc.client.enps.getConfigResults.queryOptions({
			enpsConfigId: enpsId as string,
			organizationId: organizationId as string,
		}),
		enabled: !!enpsId && !!organizationId,
	})

	// Get latest execution for company-grouped results (works for active or closed surveys)
	const { data: latestExecution } = useQuery({
		...trpc.client.enps.getLatestExecution.queryOptions({
			enpsConfigId: enpsId as string,
		}),
		enabled: !!enpsId && viewMode === 'byCompany',
	})

	const { data: companyResults, isLoading: isLoadingCompanyResults } = useQuery({
		...trpc.client.enps.getResultsGroupedByCompany.queryOptions({
			executionId: latestExecution?.id || '',
		}),
		enabled: !!latestExecution?.id && viewMode === 'byCompany',
	})

	const toggleCompanyExpanded = (company: string) => {
		setExpandedCompanies((prev) => {
			const next = new Set(prev)
			if (next.has(company)) {
				next.delete(company)
			} else {
				next.add(company)
			}
			return next
		})
	}

	// Filter out unsubmitted responses (must be before early returns per React hooks rules)
	const submittedResponses = useMemo(
		() => results?.responses.filter((response) => Boolean(response.submittedAt)) ?? [],
		[results?.responses],
	)

	// Filter and sort companies based on selected company and sort order
	const filteredCompanyResults = useMemo(() => {
		let filtered = companyResults?.byCompany ?? []

		// Apply company filter
		if (selectedCompany !== 'all') {
			filtered = filtered.filter((company) => company.company === selectedCompany)
		}

		if (companySortOrder !== 'none') {
			filtered = [...filtered].sort((a, b) => {
				const scoreA = a.score.npsScore
				const scoreB = b.score.npsScore
				if (scoreA !== scoreB) {
					return companySortOrder === 'desc' ? scoreB - scoreA : scoreA - scoreB
				}
				return b.score.totalResponses - a.score.totalResponses
			})
		}

		return filtered
	}, [companyResults, selectedCompany, companySortOrder])

	// Auto-expand when filtering to a single company
	useEffect(() => {
		if (selectedCompany !== 'all' && filteredCompanyResults.length === 1) {
			setExpandedCompanies(new Set([selectedCompany]))
		}
	}, [selectedCompany, filteredCompanyResults])

	if (isLoading) {
		return <SpinnerWithBackground />
	}

	if (!results || results.score.totalResponses === 0) {
		return (
			<div className='px-6 mt-6'>
				<div className='bg-gray-50 border border-gray-200 rounded-lg p-8 text-center'>
					<Text variant='medium' weight='semibold' className='text-gray-700'>
						No responses yet
					</Text>
					<Text variant='small' className='text-gray-600 mt-2'>
						Results will appear here once participants start submitting responses
					</Text>
				</div>
			</div>
		)
	}

	const { score, trend } = results

	// Determine NPS category and color
	const getNpsCategory = (npsScore: number) => {
		if (npsScore >= 50) return { label: 'Excellent', color: 'text-green-600' }
		if (npsScore >= 20) return { label: 'Good', color: 'text-blue-600' }
		if (npsScore >= 0) return { label: 'Needs Improvement', color: 'text-yellow-600' }
		return { label: 'Poor', color: 'text-red-600' }
	}

	const category = getNpsCategory(score.npsScore)

	// Show view toggle only if external participants are enabled
	const showViewToggle = enpsConfig?.allowExternalParticipants

	return (
		<div className='px-6 mt-6 max-w-6xl'>
			{/* View Toggle */}
			{showViewToggle && (
				<div className='mb-6 space-y-4'>
					<div className='flex items-center gap-4'>
						<Text variant='small' className='text-gray-600'>
							View:
						</Text>
						<div className='flex rounded-lg border border-gray-200 overflow-hidden'>
							<button
								type='button'
								onClick={() => setViewMode('overall')}
								className={`px-4 py-2 text-sm font-medium transition-colors ${
									viewMode === 'overall' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
								}`}>
								Overall
							</button>
							<button
								type='button'
								onClick={() => setViewMode('byCompany')}
								className={`px-4 py-2 text-sm font-medium transition-colors border-l ${
									viewMode === 'byCompany' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
								}`}>
								By Company
							</button>
						</div>
					</div>

					{/* Company Filter Dropdown */}
					{viewMode === 'byCompany' && companyResults && companyResults.byCompany.length > 0 && (
						<div className='space-y-3'>
							<div className='flex items-center gap-4'>
								<Text variant='small' className='text-gray-600'>
									Filter by Company:
								</Text>
								<select
									value={selectedCompany}
									onChange={(e) => setSelectedCompany(e.target.value)}
									className='px-4 py-2 text-sm border border-gray-200 rounded-lg bg-white text-gray-700 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'>
									<option value='all'>All Companies ({companyResults.byCompany.length})</option>
									{companyResults.byCompany.map((company) => (
										<option key={company.company} value={company.company}>
											{company.company} ({company.score.totalResponses} of {company.responses.length} responded)
										</option>
									))}
								</select>
							</div>

							{/* Company Sort Controls */}
							<div className='flex items-center gap-4'>
								<Text variant='small' className='text-gray-600'>
									Sort by NPS Score:
								</Text>
								<div className='flex rounded-lg border border-gray-200 overflow-hidden'>
									<button
										type='button'
										onClick={() => setCompanySortOrder('none')}
										className={`px-4 py-2 text-sm font-medium transition-colors ${
											companySortOrder === 'none' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
										}`}>
										None
									</button>
									<button
										type='button'
										onClick={() => setCompanySortOrder('desc')}
										className={`px-4 py-2 text-sm font-medium transition-colors border-l ${
											companySortOrder === 'desc' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
										}`}>
										↓ High to Low
									</button>
									<button
										type='button'
										onClick={() => setCompanySortOrder('asc')}
										className={`px-4 py-2 text-sm font-medium transition-colors border-l ${
											companySortOrder === 'asc' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
										}`}>
										↑ Low to High
									</button>
								</div>
							</div>
						</div>
					)}
				</div>
			)}

			{/* Company View */}
			{viewMode === 'byCompany' &&
				showViewToggle &&
				(isLoadingCompanyResults ? (
					<SpinnerWithBackground />
				) : !companyResults || companyResults.byCompany.length === 0 ? (
					<div className='bg-gray-50 border border-gray-200 rounded-lg p-8 text-center'>
						<Text variant='medium' weight='semibold' className='text-gray-700'>
							No external participant data
						</Text>
						<Text variant='small' className='text-gray-600 mt-2'>
							External participant results will appear here once they submit responses
						</Text>
					</div>
				) : (
					<div className='space-y-6'>
						{/* Overall External Score (only show when "All Companies" is selected) */}
						{selectedCompany === 'all' && (
							<div className='bg-white border border-gray-200 rounded-lg p-6'>
								<Text variant='medium' weight='semibold' className='mb-4'>
									External Participants - Overall
								</Text>
								<div className='grid grid-cols-1 md:grid-cols-4 gap-4'>
									<div>
										<Text variant='small' className='text-gray-600'>
											NPS Score
										</Text>
										<Text
											variant='large'
											weight='semibold'
											className={
												companyResults.overall.totalResponses === 0
													? 'text-gray-400'
													: getNpsCategory(companyResults.overall.npsScore).color
											}>
											{companyResults.overall.totalResponses === 0 ? 'N/A' : companyResults.overall.npsScore}
										</Text>
									</div>
									<div>
										<Text variant='small' className='text-gray-600'>
											Response Rate
										</Text>
										<Text variant='large' weight='semibold' className='text-gray-900'>
											{companyResults.overall.responseRate.toFixed(1)}%
										</Text>
									</div>
									<div>
										<Text variant='small' className='text-gray-600'>
											Total Responses
										</Text>
										<Text variant='large' weight='semibold' className='text-gray-900'>
											{companyResults.overall.totalResponses}
										</Text>
									</div>
									<div>
										<Text variant='small' className='text-gray-600'>
											Companies
										</Text>
										<Text variant='large' weight='semibold' className='text-gray-900'>
											{companyResults.byCompany.length}
										</Text>
									</div>
								</div>
							</div>
						)}

						{/* Company Breakdown */}
						{filteredCompanyResults.map((companyData) => (
							<div key={companyData.company} className='bg-white border border-gray-200 rounded-lg overflow-hidden'>
								<button
									type='button'
									onClick={() => toggleCompanyExpanded(companyData.company)}
									className='w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors'>
									<div className='flex items-center gap-4'>
										<Text variant='medium' weight='semibold'>
											{companyData.company}
										</Text>
										<span
											className={`px-2 py-1 rounded text-xs font-medium ${
												getNpsCategory(companyData.score.npsScore).color
											} bg-opacity-10`}>
											NPS: {companyData.score.npsScore}
										</span>
									</div>
									<div className='flex items-center gap-4'>
										<Text variant='small' className='text-gray-600'>
											{companyData.score.totalResponses} of {companyData.responses.length} responded
										</Text>
										<span
											className={`transform transition-transform ${
												expandedCompanies.has(companyData.company) ? 'rotate-180' : ''
											}`}>
											▼
										</span>
									</div>
								</button>

								{expandedCompanies.has(companyData.company) && (
									<div className='border-t border-gray-200 p-6'>
										{companyData.score.totalResponses === 0 ? (
											<div className='bg-gray-50 rounded-lg p-4 text-center'>
												<Text variant='small' className='text-gray-500'>
													No responses received yet
												</Text>
											</div>
										) : (
											<>
												<div className='grid grid-cols-3 gap-4 mb-6'>
													<div>
														<div className='flex justify-between items-center mb-1'>
															<Text variant='xsmall' className='text-green-700'>
																Promoters
															</Text>
															<Text variant='xsmall' weight='semibold' className='text-green-700'>
																{companyData.score.promotersPercentage.toFixed(1)}%
															</Text>
														</div>
														<div className='bg-gray-200 rounded-full h-1.5'>
															<div
																className='bg-green-600 h-1.5 rounded-full'
																style={{ width: `${companyData.score.promotersPercentage}%` }}
															/>
														</div>
													</div>
													<div>
														<div className='flex justify-between items-center mb-1'>
															<Text variant='xsmall' className='text-yellow-700'>
																Passives
															</Text>
															<Text variant='xsmall' weight='semibold' className='text-yellow-700'>
																{companyData.score.passivesPercentage.toFixed(1)}%
															</Text>
														</div>
														<div className='bg-gray-200 rounded-full h-1.5'>
															<div
																className='bg-yellow-500 h-1.5 rounded-full'
																style={{ width: `${companyData.score.passivesPercentage}%` }}
															/>
														</div>
													</div>
													<div>
														<div className='flex justify-between items-center mb-1'>
															<Text variant='xsmall' className='text-red-700'>
																Detractors
															</Text>
															<Text variant='xsmall' weight='semibold' className='text-red-700'>
																{companyData.score.detractorsPercentage.toFixed(1)}%
															</Text>
														</div>
														<div className='bg-gray-200 rounded-full h-1.5'>
															<div
																className='bg-red-600 h-1.5 rounded-full'
																style={{ width: `${companyData.score.detractorsPercentage}%` }}
															/>
														</div>
													</div>
												</div>

												{companyData.responses.length > 0 && (
													<div>
														<Text variant='small' weight='semibold' className='text-gray-700 mb-3'>
															Responses
														</Text>
														<EnpsFeedbackList responses={companyData.responses} initialPageSize={5} pageSize={20} />
													</div>
												)}
											</>
										)}
									</div>
								)}
							</div>
						))}
					</div>
				))}

			{/* Overall View (Original) */}
			{viewMode === 'overall' && (
				<>
					<div className='grid grid-cols-1 md:grid-cols-3 gap-6 mb-8'>
						{/* NPS Score Card */}
						<div className='bg-white border border-gray-200 rounded-lg p-6'>
							<Text variant='small' className='text-gray-600'>
								Net Promoter Score
							</Text>
							<div className='flex items-baseline mt-2'>
								<Text variant='large' weight='semibold' className={category.color}>
									{score.npsScore}
								</Text>
								{trend && (
									<Text
										variant='small'
										className={`ml-2 ${
											trend.direction === 'up'
												? 'text-green-600'
												: trend.direction === 'down'
													? 'text-red-600'
													: 'text-gray-600'
										}`}>
										{trend.direction === 'up' ? '↑' : trend.direction === 'down' ? '↓' : '→'} {Math.abs(trend.change)}
									</Text>
								)}
							</div>
							<Text variant='xsmall' className={category.color}>
								{category.label}
							</Text>
						</div>

						{/* Response Rate Card */}
						<div className='bg-white border border-gray-200 rounded-lg p-6'>
							<Text variant='small' className='text-gray-600'>
								Response Rate
							</Text>
							<Text variant='large' weight='semibold' className='text-gray-900 mt-2'>
								{score.responseRate.toFixed(1)}%
							</Text>
							<Text variant='xsmall' className='text-gray-600'>
								{score.totalResponses} responses
							</Text>
						</div>

						{/* Total Surveys Card */}
						<div className='bg-white border border-gray-200 rounded-lg p-6'>
							<Text variant='small' className='text-gray-600'>
								Total Responses
							</Text>
							<Text variant='large' weight='semibold' className='text-gray-900 mt-2'>
								{score.totalResponses}
							</Text>
						</div>
					</div>

					{/* Category Breakdown */}
					<div className='bg-white border border-gray-200 rounded-lg p-6 mb-8'>
						<Text variant='medium' weight='semibold' className='mb-4'>
							Response Distribution
						</Text>
						<div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
							{/* Promoters */}
							<div>
								<div className='flex justify-between items-center mb-2'>
									<Text variant='small' className='text-green-700'>
										Promoters (9-10)
									</Text>
									<Text variant='small' weight='semibold' className='text-green-700'>
										{score.promotersPercentage.toFixed(1)}%
									</Text>
								</div>
								<div className='bg-gray-200 rounded-full h-2'>
									<div className='bg-green-600 h-2 rounded-full' style={{ width: `${score.promotersPercentage}%` }} />
								</div>
								<Text variant='xsmall' className='text-gray-600 mt-1'>
									{score.promotersCount} responses
								</Text>
							</div>

							{/* Passives */}
							<div>
								<div className='flex justify-between items-center mb-2'>
									<Text variant='small' className='text-yellow-700'>
										Passives (7-8)
									</Text>
									<Text variant='small' weight='semibold' className='text-yellow-700'>
										{score.passivesPercentage.toFixed(1)}%
									</Text>
								</div>
								<div className='bg-gray-200 rounded-full h-2'>
									<div className='bg-yellow-500 h-2 rounded-full' style={{ width: `${score.passivesPercentage}%` }} />
								</div>
								<Text variant='xsmall' className='text-gray-600 mt-1'>
									{score.passivesCount} responses
								</Text>
							</div>

							{/* Detractors */}
							<div>
								<div className='flex justify-between items-center mb-2'>
									<Text variant='small' className='text-red-700'>
										Detractors (0-6)
									</Text>
									<Text variant='small' weight='semibold' className='text-red-700'>
										{score.detractorsPercentage.toFixed(1)}%
									</Text>
								</div>
								<div className='bg-gray-200 rounded-full h-2'>
									<div className='bg-red-600 h-2 rounded-full' style={{ width: `${score.detractorsPercentage}%` }} />
								</div>
								<Text variant='xsmall' className='text-gray-600 mt-1'>
									{score.detractorsCount} responses
								</Text>
							</div>
						</div>
					</div>

					{/* All Feedback */}
					{submittedResponses && submittedResponses.length > 0 && (
						<div className='bg-white border border-gray-200 rounded-lg p-6'>
							<Text variant='medium' weight='semibold' className='mb-4'>
								All Feedback
							</Text>
							<EnpsFeedbackList responses={submittedResponses} initialPageSize={10} pageSize={20} />
						</div>
					)}
				</>
			)}
		</div>
	)
}
